import {createContext, useContext} from 'react';

export type CartProductSource = {
    id: number;
    title: string;
    link: string;
    article: string;
    preview: string;
    count: number;
    price: {
        old: number;
        current: number;
    };
    counter: {
        min: number;
        addBy: number;
    };
    discount: boolean;
    deleted: boolean;
    optPrices?: number[][];
};

export type CartSourceData = {
    goods?: CartProductSource[];
    complects?: {
        discount: string;
        goods: CartProductSource[];
    }[];
};

export type CartProductData = CartProductSource & {
    checked: boolean;
};

export enum CartStep {
    Products = 'Products',
    Order = 'Order',
}

export type CartContextTotal = {
    /** цена корзины без скидки */
    price: number;
    /** сумма скидки на всю корзину */
    discount: number;
    /** итоговая цена с учетом скидки */
    totalPrice: number;
    /** количество позиций */
    productsTypeAmount: number;
    /** количество товаров */
    productsAmount: number;
    /** в корзине есть хотябы один товар, для которого применяется региональная скидка */
    cartHasRegionalDiscount: boolean;
};

export type CartContextType = {
    products: Record<string, CartProductData>;
    step: CartStep;
    total: CartContextTotal;
    orderFormIsValid: boolean;
    formSubmitCount: number;
    isReady: boolean;
    /** коэфициент региональной скидки */
    regionalDiscounts: number;
    /** нужно ли применять коэфициент региональной скидки к корзине */
    shouldApplyRegionalDiscount: boolean;
    orderFormIsSubmitting: boolean;
    productsIds: number[];
    setStep: (step: CartStep) => void;
    setProductCount: (id: number, count: number) => void;
    setDeleted: (id: number, deleted: boolean) => void;
    setChecked: (id: number, checked: boolean) => void;
    setAllChecked: (checked: boolean) => void;
    setOrderFormIsValid: (isValid: boolean) => void;
    setSubmitCountForm: (count: number) => void;
    setOrderFormIsSubmitting: (isValid: boolean) => void;
    addProduct: (product: CartProductData) => void;
};

export const CartContext = createContext<CartContextType>({
    products: {},
    productsIds: [],
    step: CartStep.Products,
    setStep: () => null,
    setProductCount: () => null,
    setDeleted: () => null,
    setChecked: () => null,
    setAllChecked: () => null,
    setOrderFormIsValid: () => null,
    setSubmitCountForm: () => null,
    setOrderFormIsSubmitting: () => null,
    addProduct: () => null,
    total: {
        price: 0,
        totalPrice: 0,
        discount: 0,
        productsAmount: 0,
        productsTypeAmount: 0,
        cartHasRegionalDiscount: false,
    },
    orderFormIsValid: false,
    isReady: false,
    orderFormIsSubmitting: false,
    regionalDiscounts: 1,
    formSubmitCount: 0,
    shouldApplyRegionalDiscount: true,
});

export function useCartContext() {
    return useContext(CartContext);
}
